import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    type OnDestroy,
    type OnInit,
    inject,
} from '@angular/core';
import { type ECartIconType, ESize, Widgets } from '@common/enums';
// eslint-disable-next-line @nx/enforce-module-boundaries
import type { CartIconSettingsFormType } from '@web/widgets/shop-cart/type/shop-cart-widget.type';
import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import type { FormElementFormGroup } from '@common/types/form-element-forms.type';
import type { SafeHtml } from '@angular/platform-browser';
import { DomSanitizer } from '@angular/platform-browser';
import { CART_ICON_TEMPLATES, CART_ICON_TYPES } from '@common/constants/cart-icon.const';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ShopCartIconFacade } from '@web/store/shop-cart-icon/shop-cart-icon.facade';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { RouterFacade } from '@web/store/router/router.facade';
import { FormControl, FormGroup } from '@angular/forms';
import { ERadioGroupType } from '@ui/sp-radio-group';
import { Subject, take } from 'rxjs';
import { EIconPosition } from '@common/enums/icon-position.enum';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { BlocksFacade } from '@web/store/page-structure/blocks/blocks.facade';
import { type ShopCartIconSettingsType } from '@common/types/shop-cart-icon-settings.type';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
// eslint-disable-next-line @nx/enforce-module-boundaries
import type { WebLegacyWidgetEntity } from '@web/types';
import { getUuid } from '@common/helpers';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { SectionsFacade } from '@web/store/page-structure/sections/sections.facade';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ColumnsFacade } from '@web/store/page-structure/columns/columns.facade';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { HistoryFacade } from '@web/store/history/history.facade';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ContentFacade } from '@web/store/website/content/content.facade';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { ShopCartIconHttpService } from '@common/services/REST/shop-cart-icon.http.service';
import { NgIf, NgFor, NgClass, NgStyle } from '@angular/common';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { WebColorPickerComponent } from '@web/shared/components/web-color-picker/web-color-picker.component';
import { SpInputNumComponent } from '../../sp-form-controls/sp-input-num/sp-input-num.component';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { SpRadioGroupComponent } from '../../../ui/sp-radio-group/src/sp-radio-group.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'sp-shop-cart-icon-settings',
    templateUrl: './sp-shop-cart-icon-settings.component.html',
    styleUrls: ['./sp-shop-cart-icon-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgIf,
        NgFor,
        NgClass,
        NgStyle,
        WebColorPickerComponent,
        SpInputNumComponent,
        SpRadioGroupComponent,
        TranslateModule,
    ],
})
export class SpShopCartIconSettingsComponent implements OnInit, OnDestroy {
    protected readonly shopCartIconFacade = inject(ShopCartIconFacade);
    protected readonly columnsFacade = inject(ColumnsFacade);
    protected readonly changeDetectorRef = inject(ChangeDetectorRef);
    protected readonly widgetsFacade = inject(WidgetsFacade);
    protected readonly routerFacade = inject(RouterFacade);
    protected readonly blocksFacade = inject(BlocksFacade);
    protected readonly sectionsFacade = inject(SectionsFacade);
    protected readonly sanitizer = inject(DomSanitizer);
    protected readonly historyFacade = inject(HistoryFacade);
    protected readonly contentFacade = inject(ContentFacade);
    protected readonly shopCartIconHttpService = inject(ShopCartIconHttpService);

    private unsubscribe$ = new Subject<void>();
    private prevData: Partial<ShopCartIconSettingsType> = null;
    private isIconWidgetInHeader: boolean = false;

    public readonly ERadioGroupType = ERadioGroupType;
    public readonly BORDER_RADIUS_MIN: number = 0;
    public readonly BORDER_RADIUS_MAX: number = 250;
    public readonly CART_ICON_TYPES = CART_ICON_TYPES;
    public readonly iconPosition = EIconPosition;
    public readonly blocks$ = this.blocksFacade.blocks$;

    public itemForm: FormElementFormGroup | any;
    public isHeaderEnabled: boolean = false;

    @Input() isMobile!: boolean;

    constructor() {
        this.itemForm = new FormGroup<CartIconSettingsFormType>({
            iconType: new FormControl(null),
            iconColor: new FormControl(null),
            iconBgColor: new FormControl(null),
            iconCounterColor: new FormControl(null),
            iconCounterBgColor: new FormControl(null),
            iconRounding: new FormControl(null),
            iconSize: new FormControl(null),
            iconPositionType: new FormControl(null),
        });
    }

    ngOnInit() {
        this.shopCartIconFacade.cartIconData$.pipe(take(1)).subscribe((data) => {
            this.widgetsFacade.widgets$.pipe(take(1)).subscribe((widgets) => {
                const widgetIndex = widgets.findIndex((val) => val.name === Widgets.SHOP_CART_ICON);
                this.isIconWidgetInHeader = widgetIndex > -1;

                this.prevData = data;
                this.itemForm.setValue(data);
                this.changeDetectorRef.detectChanges();
            });
        });
        this.blocks$.pipe(take(1)).subscribe((blocks) => {
            this.isHeaderEnabled = blocks[0]?.meta?.role === 'header';
            this.changeDetectorRef.detectChanges();
        });

        this.shopCartIconFacade.cartIconDataStream$.pipe(takeUntil(this.unsubscribe$)).subscribe((data) => {
            this.setCartIconWidgetVisibility(data.iconPositionType);

            this.itemForm.setValue(data, { emitEvent: false });
            this.changeDetectorRef.detectChanges();
        });
        this.historyFacade.lastNext$.subscribe((data) => {
            if (data && !('version' in data) && data?.payload?.entityType === 'store') {
                const iconData = data.payload.before.changes;
                this.prevData = iconData;
                this.itemForm.setValue(iconData, { emitEvent: false });
            }
        });
        this.historyFacade.lastPrev$.pipe(distinctUntilChanged()).subscribe((data) => {
            if (data && !('version' in data) && data?.payload?.entityType === 'store') {
                const iconData = data.payload.after.changes;
                this.prevData = iconData;
                this.itemForm.setValue(iconData, { emitEvent: false });
            }
        });

        this.itemForm.valueChanges.subscribe((currData: ShopCartIconSettingsType) => {
            this.routerFacade.routerParams$.pipe(take(1)).subscribe(({ siteId }) => {
                this.shopCartIconFacade.setChangedState(true);
                this.shopCartIconHttpService.setIconStyle(siteId, currData, true).subscribe();
                this.historyFacade.addStoreUpdateChangeToHistory(
                    {
                        id: 'cartIcon',
                        changes: this.prevData,
                    },
                    {
                        id: 'cartIcon',
                        changes: currData,
                    }
                );

                this.prevData = currData;
                this.shopCartIconFacade.saveCartIcon(currData);
            });
        });
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    public setCartIconWidgetVisibility(value: EIconPosition): void {
        if (value === EIconPosition.SITE) {
            this.widgetsFacade.widgets$.pipe(take(1)).subscribe((widgets) => {
                const widgetIndex = widgets.findIndex((val) => val.name === Widgets.SHOP_CART_ICON);

                if (widgetIndex > -1 && this.isIconWidgetInHeader) {
                    this.isIconWidgetInHeader = false;
                    const cartWidget = structuredClone(widgets[widgetIndex]);
                    this.widgetsFacade.removeWidget(cartWidget, widgetIndex, true, true);
                }
            });
        } else {
            this.sectionsFacade.sections$.pipe(take(1)).subscribe((sections) => {
                const section = sections.find((val) => val?.entitySubType === 'specific');
                const columnId = section.children[section.children.length - 1];

                this.columnsFacade.columnsEntities$.pipe(take(1)).subscribe((columns) => {
                    const index = columns[columnId].children.length;
                    const cartWidget: WebLegacyWidgetEntity = {
                        id: getUuid(),
                        name: Widgets.SHOP_CART_ICON,
                        data: null,
                        columnId: columnId,
                    };

                    if (!this.isIconWidgetInHeader) {
                        this.isIconWidgetInHeader = true;
                        this.widgetsFacade.addWidget(cartWidget, index);
                    }
                });
            });
        }
    }

    public setIconPositionType(value: any): void {
        if (value !== this.iconPositionType.value) {
            this.iconPositionType.setValue(value);
        }
    }

    public get iconType(): CartIconSettingsFormType['iconType'] {
        return this.itemForm.get('iconType') as CartIconSettingsFormType['iconType'];
    }

    public get iconColor(): CartIconSettingsFormType['iconColor'] {
        return this.itemForm.get('iconColor') as CartIconSettingsFormType['iconColor'];
    }

    public get iconBgColor(): CartIconSettingsFormType['iconBgColor'] {
        return this.itemForm.get('iconBgColor') as CartIconSettingsFormType['iconBgColor'];
    }

    public get iconCounterColor(): CartIconSettingsFormType['iconCounterColor'] {
        return this.itemForm.get('iconCounterColor') as CartIconSettingsFormType['iconCounterColor'];
    }

    public get iconCounterBgColor(): CartIconSettingsFormType['iconCounterBgColor'] {
        return this.itemForm.get('iconCounterBgColor') as CartIconSettingsFormType['iconCounterBgColor'];
    }

    public get iconRounding(): CartIconSettingsFormType['iconRounding'] {
        return this.itemForm.get('iconRounding') as CartIconSettingsFormType['iconRounding'];
    }

    public get iconSize(): CartIconSettingsFormType['iconSize'] {
        return this.itemForm.get('iconSize') as CartIconSettingsFormType['iconSize'];
    }

    public get iconPositionType(): CartIconSettingsFormType['iconPositionType'] {
        return this.itemForm.get('iconPositionType') as CartIconSettingsFormType['iconPositionType'];
    }

    public setIconSize(value: ESize): void {
        this.iconSize.setValue(value);
    }

    public setIconRoundingRounding(value: number): void {
        this.iconRounding.setValue(value);
    }

    public setIconTypeType(type: ECartIconType) {
        this.iconType.setValue(type);
    }

    public setItemColor({ value }: ColorPickerEventPayload, formElement: FormControl): void {
        formElement.patchValue(value);
    }

    public getIconStyles() {
        return {
            borderRadius: `${this.iconRounding.value}px`,
            background: this.iconPositionType.value === this.iconPosition.SITE ? this.iconBgColor.value : 'transparent',
            boxShadow:
                this.iconPositionType.value === this.iconPosition.SITE ? '0px 0px 16px rgba(51, 51, 51, 0.08)' : 'none',
            color: this.iconColor.value,
            height: this.previewIconSize(this.iconSize.value),
            width: this.previewIconSize(this.iconSize.value),
        };
    }

    public getCounterStyles() {
        return {
            background: this.iconCounterBgColor.value,
            color: this.iconCounterColor.value,
        };
    }

    public previewIconSize(size: ESize): string {
        switch (size) {
            case ESize.S:
                return '40px';
            case ESize.M:
                return '48px';
            case ESize.L:
                return '56px';
            default:
                return '48px';
        }
    }

    public previewIconSizeStyle() {
        return {
            height: `${this.previewIconImgSize(this.iconSize.value)}px`,
            width: `${this.previewIconImgSize(this.iconSize.value)}px`,
        };
    }

    public previewIconImgSize(size: ESize): number {
        switch (size) {
            case ESize.S:
                return 19;
            case ESize.M:
                return 24;
            case ESize.L:
                return 29;
            default:
                return 24;
        }
    }

    public getIcon(iconName: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(
            CART_ICON_TEMPLATES(iconName, this.previewIconImgSize(this.iconSize.value))
        );
    }
}
